import React from 'react'

import {
  BenefitRow,
  BenefitSortKey,
} from '../../../api/reconciliation-v2-api-client'
import {
  EMPLOYEE_COL_WIDTH,
  HideableColKey,
  HIDEABLE_COLUMNS,
  MONTHS,
  ZENDESK_COL_WIDTH,
} from './helpers'
import ColumnsMenu from './ColumnsMenu'
import { SortSvg } from './icons'
import styles from './BenefitsTable.module.scss'

/**
 * The table <thead>. Imports the SHARED BenefitsTable.module.scss so its hashed
 * sticky/sortable/num classes match the body cells (hard rule #1), and renders
 * the show/hide-columns menu inside the Action <th>.
 */

interface InfoColumn {
  key: Exclude<HideableColKey, 'employeeName'>
  label: string
  sortKey?: BenefitSortKey
  minWidth: number
  nowrap?: boolean
  render: (row: BenefitRow) => React.ReactNode
}

interface TableHeadProps {
  employeeVisible: boolean
  visibleInfoCols: InfoColumn[]
  visibleMonthIndices: number[]
  sortDir: (key: BenefitSortKey) => 'asc' | 'desc' | null
  onSort: (key: BenefitSortKey) => void
  hiddenCols: Set<HideableColKey>
  onToggleCol: (key: HideableColKey) => void
  onToggleAll: () => void
}

const TableHead: React.FC<TableHeadProps> = ({
  employeeVisible,
  visibleInfoCols,
  visibleMonthIndices,
  sortDir,
  onSort,
  hiddenCols,
  onToggleCol,
  onToggleAll,
}) => {
  const sortHeaderSpan = (label: string, key: BenefitSortKey) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {label} <SortSvg dir={sortDir(key)} />
    </span>
  )

  return (
    <thead>
      <tr>
        <th
          className={`${styles['sticky-insured']} ${styles.sortable}`}
          onClick={() => onSort(BenefitSortKey.InsuredPersons)}
        >
          {sortHeaderSpan('Insured Person', BenefitSortKey.InsuredPersons)}
        </th>
        {employeeVisible && (
          <th
            className={styles.sortable}
            style={{
              width: EMPLOYEE_COL_WIDTH,
              minWidth: EMPLOYEE_COL_WIDTH,
              maxWidth: EMPLOYEE_COL_WIDTH,
            }}
            onClick={() => onSort(BenefitSortKey.EmployeeName)}
          >
            {sortHeaderSpan('Employee', BenefitSortKey.EmployeeName)}
          </th>
        )}
        {visibleInfoCols.map((c) => (
          <th
            key={c.key}
            className={c.sortKey ? styles.sortable : undefined}
            onClick={c.sortKey ? () => onSort(c.sortKey!) : undefined}
          >
            {c.sortKey ? sortHeaderSpan(c.label, c.sortKey) : c.label}
          </th>
        ))}
        <th className={`${styles['sticky-expected']} ${styles.num}`}>
          Expected
        </th>
        {visibleMonthIndices.map((mi) => (
          <th key={mi} className={`${styles.mcol} ${styles.num}`}>
            {MONTHS[mi]}
          </th>
        ))}
        <th
          className={styles.num}
          style={{
            padding: '10px 12px',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          Sum Exp
        </th>
        <th
          className={styles.num}
          style={{
            padding: '10px 12px',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          Sum Paid
        </th>
        <th
          className={styles.num}
          style={{
            padding: '10px 12px',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          Diff
        </th>
        <th
          style={{
            width: ZENDESK_COL_WIDTH,
            minWidth: ZENDESK_COL_WIDTH,
            padding: '10px 12px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 6,
            }}
          >
            Action
            <ColumnsMenu
              columns={HIDEABLE_COLUMNS}
              hiddenCols={hiddenCols}
              onToggleCol={onToggleCol}
              onToggleAll={onToggleAll}
            />
          </div>
        </th>
      </tr>
    </thead>
  )
}

export default TableHead
